/**
 * Supabase utility functions for type-safe data extraction
 *
 * These helpers handle common patterns when working with Supabase queries,
 * particularly around aggregate functions and relation data.
 */

/**
 * Extract count from Supabase aggregate query results
 *
 * Supabase returns aggregate results in different shapes:
 * - `[{ count: 5 }]` when using `.select('relation(count)')`
 * - `{ count: 5 }` in some join scenarios
 *
 * This helper safely extracts the count value regardless of shape.
 *
 * @example
 * const { data } = await supabase.from('events').select('*, event_rsvps(count)');
 * const rsvpCount = extractCount(data.event_rsvps); // returns number
 */
export function extractCount(aggregateResult: unknown): number {
  if (aggregateResult == null) return 0;

  // Array format: [{ count: N }]
  if (Array.isArray(aggregateResult)) {
    const first = aggregateResult[0];
    if (first && typeof first === 'object' && 'count' in first) {
      const count = (first as { count: unknown }).count;
      return typeof count === 'number' ? count : Number(count) || 0;
    }
    return 0;
  }

  // Object format: { count: N }
  if (typeof aggregateResult === 'object' && 'count' in aggregateResult) {
    const count = (aggregateResult as { count: unknown }).count;
    return typeof count === 'number' ? count : Number(count) || 0;
  }

  return 0;
}
